import {
  DescriptionList,
  DescriptionListItem,
} from "@/components/DescriptionList";
import { currencyFormatter } from "@/utils/currencyFormatter";
import styled from "@emotion/styled";
import { useOrder } from "@repo/api";
import { Skeleton, Text, Variables } from "@repo/skapa";
import { getLanguageLocal, t, useTranslation } from "@repo/translation";
import { Line, localizeDateAndTime, useSettings } from "@repo/ui";

export interface WaitingListDetailsProps {
  orderId?: string;
}

const TwoColumnList = styled(DescriptionList)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Variables.space75};

  @media (min-width: ${Variables.breakpointL}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: ${Variables.breakpointM}) {
    gap: ${Variables.space150};
  }
`;

const ImageList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${Variables.space75};
  list-style: none;
`;

const OrderImage = styled.img`
  width: 7rem;
  height: 7rem;
`;

export const WaitingListDetails = ({ orderId }: WaitingListDetailsProps) => {
  const { data: order, isLoading } = useOrder(orderId);
  const { market, language, general } = useSettings();
  const { dateFnsLocale } = useTranslation();

  return (
    <>
      <Line />
      <Text textStyle="Heading.XS">{t("label.waiting_list_details")}</Text>

      {isLoading && <Skeleton height="2rem" width="100%" />}

      {!isLoading && order && (
        <>
          <TwoColumnList>
            <DescriptionListItem
              title={t("label.order_id")}
              description={order.orderNo?.toString()}
            />
            <DescriptionListItem
              title={t("label.total_articles")}
              description={order.items?.length ?? "-"}
            />
            <DescriptionListItem
              title={t("label.value")}
              description={currencyFormatter({
                value: order.orderValue ?? 0,
                currency: order.currencyCode,
                locale: getLanguageLocal(language, market),
                showSign: true,
                disableDecimals: Boolean(
                  general?.localization?.disable_decimals
                ),
                fractions: 2,
              })}
            />
            <DescriptionListItem
              title={t("label.created")}
              description={localizeDateAndTime(
                order.orderCreationDate,
                dateFnsLocale
              )}
            />
          </TwoColumnList>
          <ImageList>
            {order.items?.map((item) => {
              const href = item.imageVariants?.[0]?.href;
              if (!href) {
                return null;
              }

              return (
                <li key={item.itemNo}>
                  <OrderImage src={href} alt={item.itemName} />
                </li>
              );
            })}
          </ImageList>
        </>
      )}
    </>
  );
};
